<template>
  <div class="entity-list-page lms-admin-page">
    <header class="list-header">
      <div>
        <h1>Manage courses</h1>
        <p class="list-sub">Manage your organization's courses, categories, and content</p>
      </div>
      <div class="header-actions">
        <router-link to="/learning/engagement" class="btn btn-ghost">Engagement</router-link>
        <router-link :to="{ path: '/learning', query: { tab: 'browse' } }" class="btn btn-ghost">Catalog</router-link>
      </div>
    </header>

    <!-- Tab Navigation -->
    <div class="admin-tabs">
      <button
        @click="activeTab = 'courses'"
        :class="{ active: activeTab === 'courses' }"
        class="tab-button"
      >
        <i class="fas fa-book"></i>
        Courses
      </button>
      <button
        @click="activeTab = 'categories'"
        :class="{ active: activeTab === 'categories' }"
        class="tab-button"
      >
        <i class="fas fa-folder"></i>
        Categories
      </button>
      <button
        @click="activeTab = 'videos'"
        :class="{ active: activeTab === 'videos' }"
        class="tab-button"
      >
        <i class="fas fa-video"></i>
        Videos
      </button>
      <button
        @click="activeTab = 'live-events'"
        :class="{ active: activeTab === 'live-events' }"
        class="tab-button"
      >
        <i class="fas fa-broadcast-tower"></i>
        Live Events
      </button>
      <button
        @click="activeTab = 'reports'"
        :class="{ active: activeTab === 'reports' }"
        class="tab-button"
      >
        <i class="fas fa-file-export"></i>
        Reports
      </button>
      <button
        @click="activeTab = 'race'"
        :class="{ active: activeTab === 'race' }"
        class="tab-button race-tab"
      >
        <i class="fas fa-certificate"></i>
        RACE Approvals
      </button>
      <button
        @click="activeTab = 'speakers'"
        :class="{ active: activeTab === 'speakers' }"
        class="tab-button"
      >
        <i class="fas fa-chalkboard-teacher"></i>
        Speakers
      </button>
      <button
        @click="activeTab = 'members'"
        :class="{ active: activeTab === 'members' }"
        class="tab-button"
      >
        <i class="fas fa-id-card"></i>
        Members
      </button>
      <button
        @click="activeTab = 'author-content'"
        :class="{ active: activeTab === 'author-content' }"
        class="tab-button"
      >
        <i class="fas fa-photo-video"></i>
        Author Portal
      </button>
      <button
        @click="activeTab = 'engagement'"
        :class="{ active: activeTab === 'engagement' }"
        class="tab-button engagement-tab"
      >
        <i class="fas fa-chart-line"></i>
        Engagement
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Courses Tab -->
      <div v-show="activeTab === 'courses'" class="tab-pane">
        <CourseManager />
      </div>

      <!-- Categories Tab -->
      <div v-show="activeTab === 'categories'" class="tab-pane">
        <CategoryManager />
      </div>

      <!-- Videos Tab -->
      <div v-show="activeTab === 'videos'" class="tab-pane">
        <VideoManager />
      </div>

      <!-- Live Events Tab -->
      <div v-show="activeTab === 'live-events'" class="tab-pane">
        <LiveEventManager />
      </div>

      <!-- Reports Tab -->
      <div v-show="activeTab === 'reports'" class="tab-pane">
        <div class="reports-section">
          <h2 class="section-title">
            <i class="fas fa-clipboard-list"></i>
            Quiz & Certification Reports
          </h2>
          <p class="section-description">
            Export quiz data for RACE certification review and compliance reporting.
          </p>

          <div class="report-cards">
            <!-- Quiz Questions Export -->
            <div class="report-card">
              <div class="report-icon">
                <i class="fas fa-question-circle"></i>
              </div>
              <div class="report-info">
                <h3>Quiz Questions Export</h3>
                <p>Export all quiz questions from courses and videos including correct answers, explanations, and timestamps.</p>
                <ul class="report-includes">
                  <li><i class="fas fa-check"></i> Course-level quizzes</li>
                  <li><i class="fas fa-check"></i> Video-level quizzes</li>
                  <li><i class="fas fa-check"></i> Correct answers & explanations</li>
                  <li><i class="fas fa-check"></i> Passing score requirements</li>
                </ul>
              </div>
              <div class="report-actions">
                <button 
                  @click="exportQuizzes" 
                  :disabled="exporting.quizzes"
                  class="btn btn-primary"
                >
                  <i :class="exporting.quizzes ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
                  {{ exporting.quizzes ? 'Exporting...' : 'Download CSV' }}
                </button>
              </div>
            </div>

            <!-- Quiz Attempts Export -->
            <div class="report-card">
              <div class="report-icon">
                <i class="fas fa-user-graduate"></i>
              </div>
              <div class="report-info">
                <h3>Quiz Attempts Export</h3>
                <p>Export learner quiz attempt history with scores, pass/fail status, and completion times.</p>
                <ul class="report-includes">
                  <li><i class="fas fa-check"></i> User details & emails</li>
                  <li><i class="fas fa-check"></i> Score percentages</li>
                  <li><i class="fas fa-check"></i> Pass/fail status</li>
                  <li><i class="fas fa-check"></i> Attempt timestamps</li>
                </ul>
              </div>
              <div class="report-actions">
                <button 
                  @click="exportQuizAttempts" 
                  :disabled="exporting.attempts"
                  class="btn btn-primary"
                >
                  <i :class="exporting.attempts ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
                  {{ exporting.attempts ? 'Exporting...' : 'Download CSV' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Export Status Message -->
          <div v-if="exportMessage" :class="['export-message', exportMessage.type]">
            <i :class="exportMessage.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
            {{ exportMessage.text }}
          </div>
        </div>
      </div>

      <!-- RACE Approvals Tab -->
      <div v-show="activeTab === 'race'" class="tab-pane">
        <RACEApprovalManager />
      </div>

      <!-- Speakers Tab -->
      <div v-show="activeTab === 'speakers'" class="tab-pane">
        <SpeakerManager />
      </div>

      <!-- Author Content Portal Tab -->
      <div v-show="activeTab === 'author-content'" class="tab-pane">
        <AuthorContentManager />
      </div>

      <!-- Engagement Tab -->
      <div v-show="activeTab === 'engagement'" class="tab-pane">
        <EngagementDashboard />
      </div>

      <!-- Members Tab -->
      <div v-show="activeTab === 'members'" class="tab-pane">
        <div class="members-section">
          <h2 class="section-title">
            <i class="fas fa-users"></i>
            Member Verification Tools
          </h2>
          <p class="section-description">
            Verify if an email address exists in the SCAV Member Clicks database. Use this to troubleshoot when users report membership verification issues.
          </p>

          <!-- Member Clicks Verification Card -->
          <div class="verification-card">
            <div class="verification-header">
              <div class="verification-icon">
                <i class="fas fa-search"></i>
              </div>
              <div>
                <h3>Check Member Clicks Email</h3>
                <p>Enter an email address to verify if it exists in the SCAV membership database and check their status.</p>
              </div>
            </div>

            <div class="verification-form">
              <div class="input-group">
                <label for="member-email">Email Address</label>
                <div class="input-with-button">
                  <input
                    id="member-email"
                    v-model="memberEmail"
                    type="email"
                    placeholder="user@example.com"
                    @keyup.enter="verifyMemberEmail"
                    :disabled="verifying"
                  />
                  <button
                    @click="verifyMemberEmail"
                    :disabled="!memberEmail || verifying"
                    class="btn btn-primary"
                  >
                    <i :class="verifying ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
                    {{ verifying ? 'Checking...' : 'Verify' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Verification Result -->
            <div v-if="memberResult" class="verification-result" :class="memberResult.resultClass">
              <div class="result-header">
                <i :class="memberResult.icon"></i>
                <span class="result-title">{{ memberResult.title }}</span>
              </div>
              
              <div v-if="memberResult.success" class="result-details">
                <div class="result-row">
                  <span class="label">Email:</span>
                  <span class="value">{{ memberResult.email }}</span>
                </div>
                <div class="result-row">
                  <span class="label">Found in Member Clicks:</span>
                  <span class="value" :class="memberResult.found_in_memberclicks ? 'text-success' : 'text-warning'">
                    {{ memberResult.found_in_memberclicks ? 'Yes' : 'No' }}
                  </span>
                </div>
                <div v-if="memberResult.found_in_memberclicks" class="result-row">
                  <span class="label">Active Member:</span>
                  <span class="value" :class="memberResult.is_active_member ? 'text-success' : 'text-warning'">
                    {{ memberResult.is_active_member ? 'Yes ✓' : 'No' }}
                  </span>
                </div>
                <div v-if="memberResult.member_status" class="result-row">
                  <span class="label">Member Status:</span>
                  <span class="value">{{ memberResult.member_status }}</span>
                </div>
                <div v-if="memberResult.member_type" class="result-row">
                  <span class="label">Member Type:</span>
                  <span class="value">{{ memberResult.member_type }}</span>
                </div>
                <div v-if="memberResult.first_name || memberResult.last_name" class="result-row">
                  <span class="label">Name:</span>
                  <span class="value">{{ memberResult.first_name }} {{ memberResult.last_name }}</span>
                </div>
                <div v-if="memberResult.profile_id" class="result-row">
                  <span class="label">Profile ID:</span>
                  <span class="value">{{ memberResult.profile_id }}</span>
                </div>
                <div v-if="memberResult.reason && !memberResult.is_active_member" class="result-row">
                  <span class="label">Reason:</span>
                  <span class="value text-warning">{{ memberResult.reason }}</span>
                </div>
              </div>

              <div v-else class="result-error">
                <p><strong>Error:</strong> {{ memberResult.error || 'Unknown error' }}</p>
                <p v-if="memberResult.message">{{ memberResult.message }}</p>
                <div v-if="memberResult.troubleshooting" class="troubleshooting">
                  <p><strong>Troubleshooting:</strong></p>
                  <ul>
                    <li v-for="(step, idx) in memberResult.troubleshooting" :key="idx">{{ step }}</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Help Text -->
            <div class="verification-help">
              <i class="fas fa-info-circle"></i>
              <div>
                <strong>Common Issues:</strong>
                <ul>
                  <li>User may have registered with a different email in Member Clicks</li>
                  <li>Username field vs email field confusion - check both with the member</li>
                  <li>Membership may have expired or be in a non-active status</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import CategoryManager from '@/components/lms/admin/CategoryManager.vue'
import CourseManager from '@/components/lms/admin/CourseManager.vue'
import VideoManager from '@/components/lms/admin/VideoManager.vue'
import RACEApprovalManager from '@/components/lms/admin/RACEApprovalManager.vue'
import SpeakerManager from '@/components/lms/admin/SpeakerManager.vue'
import LiveEventManager from '@/components/lms/admin/LiveEventManager.vue'
import AuthorContentManager from '@/components/lms/admin/AuthorContentManager.vue'
import EngagementDashboard from '@/components/lms/engagement/EngagementDashboard.vue'
import api from '@/services/api'

export default {
  name: 'LMSAdmin',
  components: {
    CategoryManager,
    CourseManager,
    VideoManager,
    RACEApprovalManager,
    SpeakerManager,
    LiveEventManager,
    AuthorContentManager,
    EngagementDashboard
  },
  setup() {
    const activeTab = ref('courses')
    const exporting = reactive({
      quizzes: false,
      attempts: false
    })
    const exportMessage = ref(null)
    
    // Member verification state
    const memberEmail = ref('')
    const verifying = ref(false)
    const memberResult = ref(null)

    const downloadFile = (blob, filename) => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    }

    const exportQuizzes = async () => {
      exporting.quizzes = true
      exportMessage.value = null
      
      try {
        const response = await api.get('/lms/dashboard/export-quizzes/', {
          responseType: 'blob'
        })
        
        const filename = `lms_quizzes_export_${new Date().toISOString().split('T')[0]}.csv`
        downloadFile(response.data, filename)
        
        exportMessage.value = {
          type: 'success',
          text: 'Quiz questions exported successfully!'
        }
      } catch (error) {
        console.error('Error exporting quizzes:', error)
        exportMessage.value = {
          type: 'error',
          text: 'Failed to export quiz questions. Please try again.'
        }
      } finally {
        exporting.quizzes = false
        // Clear message after 5 seconds
        setTimeout(() => {
          if (exportMessage.value) exportMessage.value = null
        }, 5000)
      }
    }

    const exportQuizAttempts = async () => {
      exporting.attempts = true
      exportMessage.value = null
      
      try {
        const response = await api.get('/lms/dashboard/export-quiz-attempts/', {
          responseType: 'blob'
        })
        
        const filename = `quiz_attempts_export_${new Date().toISOString().split('T')[0]}.csv`
        downloadFile(response.data, filename)
        
        exportMessage.value = {
          type: 'success',
          text: 'Quiz attempts exported successfully!'
        }
      } catch (error) {
        console.error('Error exporting quiz attempts:', error)
        exportMessage.value = {
          type: 'error',
          text: 'Failed to export quiz attempts. Please try again.'
        }
      } finally {
        exporting.attempts = false
        // Clear message after 5 seconds
        setTimeout(() => {
          if (exportMessage.value) exportMessage.value = null
        }, 5000)
      }
    }

    const verifyMemberEmail = async () => {
      if (!memberEmail.value) return
      
      verifying.value = true
      memberResult.value = null
      
      try {
        const response = await api.post('/users/admin/verify-scav-member/', {
          email: memberEmail.value.trim().toLowerCase()
        })
        
        const data = response.data
        
        // Add UI-friendly properties
        if (data.success) {
          if (data.is_active_member) {
            data.resultClass = 'result-success'
            data.icon = 'fas fa-check-circle'
            data.title = 'Active SCAV Member'
          } else if (data.found_in_memberclicks) {
            data.resultClass = 'result-warning'
            data.icon = 'fas fa-exclamation-triangle'
            data.title = 'Found but Not Active'
          } else {
            data.resultClass = 'result-not-found'
            data.icon = 'fas fa-user-slash'
            data.title = 'Not Found in Member Clicks'
          }
        } else {
          data.resultClass = 'result-error'
          data.icon = 'fas fa-times-circle'
          data.title = 'Verification Error'
        }
        
        memberResult.value = data
      } catch (error) {
        console.error('Error verifying member:', error)
        const errorData = error.response?.data || {}
        memberResult.value = {
          success: false,
          resultClass: 'result-error',
          icon: 'fas fa-times-circle',
          title: 'Verification Failed',
          error: errorData.error || 'Failed to verify member',
          message: errorData.message || 'An error occurred while checking the Member Clicks API.',
          troubleshooting: errorData.troubleshooting
        }
      } finally {
        verifying.value = false
      }
    }

    return {
      activeTab,
      exporting,
      exportMessage,
      exportQuizzes,
      exportQuizAttempts,
      memberEmail,
      verifying,
      memberResult,
      verifyMemberEmail
    }
  }
}
</script>

<style scoped>
.lms-admin-page {
  padding-bottom: 2.5rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  text-decoration: none;
  border: 1px solid #e2e8f0;
  background: white;
}

.btn-ghost:hover {
  color: var(--color-primary, #1e3a5f);
  border-color: #cbd5e1;
}

.admin-tabs {
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-xl);
  border-bottom: 2px solid var(--color-border);
}

.tab-button {
  padding: var(--spacing-md) var(--spacing-xl);
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: 1rem;
}

.tab-button:hover {
  color: var(--color-primary);
  background: rgba(0, 0, 0, 0.02);
}

.tab-button.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
  background: rgba(var(--color-primary-rgb), 0.05);
}

.tab-button.race-tab.active {
  color: #059669;
  border-bottom-color: #059669;
  background: rgba(5, 150, 105, 0.05);
}

.tab-button.engagement-tab.active {
  color: #7c3aed;
  border-bottom-color: #7c3aed;
  background: rgba(124, 58, 237, 0.05);
}

.tab-content {
  background: white;
  border-radius: var(--border-radius);
  padding: var(--spacing-xl);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tab-pane {
  min-height: 400px;
}

/* Reports Section Styles */
.reports-section {
  padding: var(--spacing-md);
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-sm) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.section-title i {
  color: var(--color-primary);
}

.section-description {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-xl) 0;
  font-size: 1rem;
}

.report-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: var(--spacing-xl);
}

.report-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.report-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.report-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--color-primary), var(--color-primary-dark, #1d4ed8));
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
}

.report-icon i {
  font-size: 1.75rem;
  color: white;
}

.report-info h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-sm) 0;
}

.report-info p {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-md) 0;
  line-height: 1.5;
}

.report-includes {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xs);
}

.report-includes li {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.report-includes li i {
  color: var(--color-success, #22c55e);
  font-size: 0.75rem;
}

.report-actions {
  margin-top: auto;
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-border);
}

.report-actions .btn {
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.btn-primary {
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-dark, #1d4ed8);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.export-message {
  margin-top: var(--spacing-xl);
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-weight: 500;
}

.export-message.success {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.export-message.error {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

/* Members Section Styles */
.members-section {
  padding: var(--spacing-md);
}

.verification-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  max-width: 700px;
}

.verification-header {
  display: flex;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
}

.verification-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.verification-icon i {
  font-size: 1.75rem;
  color: white;
}

.verification-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.verification-header p {
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.verification-form {
  margin-bottom: var(--spacing-xl);
}

.input-group label {
  display: block;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
}

.input-with-button {
  display: flex;
  gap: var(--spacing-sm);
}

.input-with-button input {
  flex: 1;
  padding: var(--spacing-md);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-with-button input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.1);
}

.input-with-button input:disabled {
  background: var(--color-surface-secondary);
  cursor: not-allowed;
}

.input-with-button .btn {
  padding: var(--spacing-md) var(--spacing-lg);
  white-space: nowrap;
}

/* Verification Result Styles */
.verification-result {
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
}

.verification-result.result-success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.verification-result.result-warning {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.verification-result.result-not-found {
  background: rgba(107, 114, 128, 0.1);
  border: 1px solid rgba(107, 114, 128, 0.3);
}

.verification-result.result-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.result-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
}

.result-header i {
  font-size: 1.5rem;
}

.result-success .result-header i { color: #16a34a; }
.result-warning .result-header i { color: #d97706; }
.result-not-found .result-header i { color: #6b7280; }
.result-error .result-header i { color: #dc2626; }

.result-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.result-success .result-title { color: #16a34a; }
.result-warning .result-title { color: #d97706; }
.result-not-found .result-title { color: #4b5563; }
.result-error .result-title { color: #dc2626; }

.result-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.result-row {
  display: flex;
  gap: var(--spacing-md);
}

.result-row .label {
  font-weight: 500;
  color: var(--color-text-secondary);
  min-width: 160px;
}

.result-row .value {
  color: var(--color-text-primary);
}

.result-row .value.text-success {
  color: #16a34a;
  font-weight: 500;
}

.result-row .value.text-warning {
  color: #d97706;
  font-weight: 500;
}

.result-error p {
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--color-text-primary);
}

.troubleshooting {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid rgba(239, 68, 68, 0.2);
}

.troubleshooting ul {
  margin: var(--spacing-sm) 0 0 0;
  padding-left: var(--spacing-lg);
}

.troubleshooting li {
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-xs);
}

/* Help Text */
.verification-help {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: var(--border-radius);
}

.verification-help > i {
  color: #3b82f6;
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.verification-help strong {
  color: var(--color-text-primary);
  display: block;
  margin-bottom: var(--spacing-xs);
}

.verification-help ul {
  margin: 0;
  padding-left: var(--spacing-lg);
}

.verification-help li {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: var(--spacing-xs);
}

@media (max-width: 768px) {
  .page-title {
    font-size: 1.5rem;
  }

  .admin-tabs {
    flex-direction: column;
    border-bottom: none;
  }

  .tab-button {
    width: 100%;
    justify-content: flex-start;
    border-bottom: 1px solid var(--color-border);
  }

  .tab-button.active {
    border-left: 3px solid var(--color-primary);
    border-bottom: 1px solid var(--color-border);
  }

  .report-cards {
    grid-template-columns: 1fr;
  }

  .report-includes {
    grid-template-columns: 1fr;
  }

  .verification-header {
    flex-direction: column;
  }

  .input-with-button {
    flex-direction: column;
  }

  .result-row {
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  .result-row .label {
    min-width: auto;
  }
}
</style>

